import { DateTime } from 'luxon';

export type HolidayNames =
  | 'Cinco de Mayo'
  | 'Earth Day'
  | 'Easter'
  | 'Halloween'
  | 'Martin Luther King Jr. Day'
  | "New Year's Day"
  | "New Year's Eve"
  | 'Patriot Day'
  | 'Super Bowl Sunday'
  | 'Star Wars Day'
  | "St. Patrick's Day"
  | "Valentine's Day"
  | "Women's Day";

const staticNationalHolidays = [
  {
    logo: '/next-assets/images/logos/bz-logo-nye.gif',
    name: "New Year's Day",
    timeFrame: ['01-01'],
  },
  {
    logo: '/next-assets/images/logos/bz-logo-valentine.gif',
    mobileLogo: '/next-assets/images/logos/bz-logo-mobile-valentine.gif',
    name: "Valentine's Day",
    timeFrame: ['02-14'],
  },
  {
    logo: '/next-assets/images/logos/bz-logo-womens-day.gif',
    mobileLogo: '/next-assets/images/logos/bz-logo-mobile-womens-day.gif',
    name: "Women's Day",
    timeFrame: ['03-08'],
  },
  {
    logo: '/next-assets/images/logos/bz-logo-saint-patrick-day.gif',
    mobileLogo: '/next-assets/images/logos/bz-logo-mobile-saint-patrick-day.gif',
    name: "St. Patrick's Day",
    timeFrame: ['03-17'],
  },
  {
    logo: '/next-assets/images/logos/bz-logo-earth-day.gif',
    mobileLogo: '/next-assets/images/logos/bz-logo-mobile-earth-day.gif',
    name: 'Earth Day',
    timeFrame: ['04-22'],
  },
  {
    logo: '/next-assets/images/logos/bz-logo-star-wars-day.gif',
    mobileLogo: '/next-assets/images/logos/bz-logo-mobile-star-wars-day.gif',
    name: 'Star Wars Day',
    timeFrame: ['05-04'],
  },
  {
    logo: '/next-assets/images/logos/bz-logo-cinco-de-mayo.gif',
    mobileLogo: '/next-assets/images/logos/bz-logo-mobile-cinco-de-mayo.gif',
    name: 'Cinco de Mayo',
    timeFrame: ['05-05'],
  },
  {
    logo: '/next-assets/images/logos/bz-logo-halloween.gif',
    name: 'Halloween',
    timeFrame: ['10-13', '10-31'],
  },
  {
    logo: '/next-assets/images/logos/bz-logo-nye.gif',
    name: "New Year's Eve",
    timeFrame: ['12-31'],
  },
];

// Third Monday of January
const isMlkDay = DateTime.now().weekNumber === 3 && DateTime.now().weekday === 1;

const checkIfShouldUseSuperBowlSundayLogo = () => {
  const getSecondSundayDateLuxon = () => {
    const year = new Date().getFullYear();
    const start = new Date(year, 1, 7);
    const secondSunday = 7 + (7 - start.getDay());
    const secondSundayDateLuxon = DateTime.fromJSDate(new Date(year, 1, secondSunday));
    return secondSundayDateLuxon;
  };
  const dayBeforeSecondSunday = getSecondSundayDateLuxon().minus({ days: 1 }).toFormat('yyyy-MM-dd');
  const secondSundayDateString = getSecondSundayDateLuxon().toFormat('yyyy-MM-dd');
  const today = DateTime.now().toFormat('yyyy-MM-dd');
  return today === secondSundayDateString || today === dayBeforeSecondSunday;
};

// Second Sunday of February
const isSuperBowlSundayDay = checkIfShouldUseSuperBowlSundayLogo();

const calculateEasterDayForCurrentYear = (): string => {
  // All calculations done on the basis of Gauss Easter Algorithm
  const year = new Date().getFullYear();

  const A = year % 19;
  const B = year % 4;
  const C = year % 7;
  const P = Math.floor(year / 100.0);

  const Q = Math.floor((13 + 8 * P) / 25.0);
  const M = Math.floor(15 - Q + P - Math.floor(P / 4)) % 30;
  const N = Math.floor(4 + P - Math.floor(P / 4)) % 7;
  const D = Math.floor(19 * A + M) % 30;
  const E = Math.floor(2 * B + 4 * C + 6 * D + N) % 7;

  const days = Math.floor(22 + D + E);

  let result = '';

  // A corner case,
  // when D is 29
  if (D === 29 && E === 6) {
    result = year + '-04-19';
  }
  // Another corner case,
  // when D is 28
  else if (D === 28 && E === 6) {
    result = year + '-04-18';
  } else {
    // If days > 31, move to April
    // April = 4th Month
    if (days > 31) {
      result = year + '-04-' + (days - 31);
    } else {
      // Otherwise, stay on March
      // March = 3rd Month
      result = year + '-03-' + days;
    }
  }

  result = DateTime.fromFormat(result, 'yyyy-MM-d').toFormat('yyyy-MM-dd');

  return result;
};

const checkIfTodayIsEaster = (): boolean => {
  const today = DateTime.now().toFormat('yyyy-MM-dd');
  const easterDay = calculateEasterDayForCurrentYear();
  return today === easterDay;
};

const isEaster = checkIfTodayIsEaster();

// Last Monday of May
const isMemorialDay = DateTime.now().weekNumber === 22 && DateTime.now().weekday === 1;

const dynamicHolidays = [
  {
    logo: '/next-assets/images/logos/bz-logo-mlk.gif',
    name: 'Martin Luther King Jr. Day',
    timeFrame: isMlkDay,
  },
  {
    logo: '/next-assets/images/logos/bz-logo-memorial.gif',
    mobileLogo: '/next-assets/images/logos/bz-logo-mobile-memorial.gif',
    name: 'Memorial Day',
    timeFrame: isMemorialDay,
  },
  {
    logo: '/next-assets/images/logos/bz-logo-super_bowl_sunday.gif',
    mobileLogo: '/next-assets/images/logos/bz-logo-mobile-super_bowl_sunday.gif',
    name: 'Super Bowl Sunday',
    timeFrame: isSuperBowlSundayDay,
  },
  {
    logo: '/next-assets/images/logos/bz-logo-easter.gif',
    mobileLogo: '/next-assets/images/logos/bz-logo-mobile-easter.gif',
    name: 'Easter',
    timeFrame: isEaster,
  },
];

export const getHolidayLogo = (device: 'desktop' | 'mobile'): string | undefined => {
  let result: string | undefined = undefined;
  const logoUrl = device === 'desktop' ? 'logo' : 'mobileLogo';
  const dateNow = DateTime.now().setZone('America/New_York');
  const dateNowString = dateNow.toFormat('MM-dd');
  const hasStaticTimeframe = staticNationalHolidays.find(holiday => {
    if (holiday.timeFrame.length === 1) {
      return holiday.timeFrame[0] === dateNowString;
    } else if (holiday.timeFrame?.[0] && holiday.timeFrame?.[1]) {
      const startDate = holiday.timeFrame[0];
      const endDate = holiday.timeFrame[1];
      const isDateBetween = dateNowString >= startDate && dateNowString <= endDate;
      return isDateBetween;
    } else {
      return false;
    }
  });
  const hasDynamicTimeframe = dynamicHolidays.find(holiday => holiday.timeFrame);
  if (hasStaticTimeframe) {
    result = hasStaticTimeframe[logoUrl];
  } else if (hasDynamicTimeframe) {
    result = hasDynamicTimeframe[logoUrl];
  }
  return result;
};

export const convertToAbsoluteUrl = (url?: string) => {
  return process.env.NODE_ENV === 'development' ? url : `https://www.benzinga.com${url}`;
};
